import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import type { ModerationDocumentKind, OrganizationKind, SupportTicketStatus } from '@/contracts';
import { adminApi, consoleApi } from './api';
import { queryKeys, type ModerationSubject } from './query-keys';
import { unwrap } from './unwrap';
import { useApiQuery } from './use-api-query';

/**
 * The console's reads, one hook each: the key from `queryKeys`, the call from the API
 * client, and the options that belong to the data rather than to a screen.
 *
 * This is the layer between the screens and `@/lib/api`. A screen asks for "the
 * talents" and gets a query; it never names the endpoint or the cache key, so two
 * screens that read the same thing cannot drift into two caches, and a structural spec
 * (`no-direct-api-in-features.spec.ts`) keeps them from going round it.
 */

const FIVE_MINUTES = 5 * 60 * 1000;
/** The shell's badges are what the queues hold right now (C5): polled, not read once. */
const BADGE_POLL = 60_000;

export const usePendingCountsQuery = () =>
  useApiQuery(queryKeys.pendingCounts(), () => adminApi.pendingCounts(), {
    refetchInterval: BADGE_POLL,
  });

export const useNotificationsQuery = () =>
  useApiQuery(queryKeys.notifications(), () => adminApi.notifications(), {
    refetchInterval: BADGE_POLL,
  });

export const useAnalyticsSummaryQuery = () =>
  useApiQuery(queryKeys.analytics.summary(), () => consoleApi.analyticsSummary());

/**
 * The growth series for the last `months`. Switching the range keeps the series on
 * screen until the new one lands, rather than dropping the chart to a spinner.
 */
export const useAnalyticsGrowthQuery = (months: number) =>
  useApiQuery(queryKeys.analytics.growth(months), () => consoleApi.analyticsGrowth(months), {
    placeholderData: keepPreviousData,
  });

export const useDemandSupplyQuery = () =>
  useApiQuery(queryKeys.analytics.demandSupply(), () => consoleApi.analyticsDemandSupply());

export const useReportQuery = (from: string | undefined, to: string | undefined) =>
  useApiQuery(queryKeys.report(from, to), () => consoleApi.report(from, to));

export const useClubActivityQuery = () =>
  useApiQuery(queryKeys.clubActivity(), () => consoleApi.clubActivity());

export const useMostSearchedQuery = () =>
  useApiQuery(queryKeys.mostSearched(), () => consoleApi.analyticsMostSearched());

export const usePaymentsQuery = () =>
  useApiQuery(queryKeys.payments(), () => consoleApi.payments());

export const usePlansQuery = () => useApiQuery(queryKeys.plans(), () => consoleApi.plans());

export const usePoliciesQuery = () =>
  useApiQuery(queryKeys.policies(), () => consoleApi.policies());

export const useAdminTeamQuery = () => useApiQuery(queryKeys.admins(), () => consoleApi.admins());

/**
 * Reference data (ADR-004), held for five minutes: it changes with a deploy, and every
 * talent screen resolves its specialty ids against it.
 */
export const useSpecialtiesQuery = () =>
  useApiQuery(queryKeys.specialties(), () => consoleApi.listSpecialties(), {
    staleTime: FIVE_MINUTES,
  });

/** Deliberately not held: an admin who has just verified somebody reloads to see it. */
export const useTalentsQuery = () =>
  useApiQuery(queryKeys.talents.all(), () => consoleApi.searchTalents());

export const useTalentQuery = (candidateId: string) =>
  useApiQuery(queryKeys.talents.detail(candidateId), () => consoleApi.talent(candidateId));

export const useOrganizationsQuery = (kind: 'all' | OrganizationKind) =>
  useApiQuery(queryKeys.organizations.list(kind), () =>
    consoleApi.organizations(kind === 'all' ? {} : { kind }),
  );

export const useOrganizationQuery = (organizationId: string) =>
  useApiQuery(queryKeys.organizations.detail(organizationId), () =>
    consoleApi.organization(organizationId),
  );

/**
 * A review queue. The document queue can be narrowed to one kind on the API
 * (`documentKind`), which pages server-side; absent, it lists both.
 */
export const useModerationQueueQuery = (
  subject: ModerationSubject,
  {
    enabled = true,
    documentKind,
  }: { enabled?: boolean; documentKind?: ModerationDocumentKind } = {},
) =>
  useApiQuery(
    documentKind
      ? queryKeys.moderation.documentKind(documentKind)
      : queryKeys.moderation.queue(subject),
    () =>
      documentKind
        ? consoleApi.moderationQueue(subject, { documentKind })
        : consoleApi.moderationQueue(subject),
    { enabled },
  );

/**
 * One queued item by id. Not retried: while the endpoint is new on the API it answers
 * 404, and the detail screen falls back to the queue list at once instead of waiting.
 */
export const useModerationItemQuery = (subject: ModerationSubject, itemId: string) =>
  useApiQuery(
    queryKeys.moderation.item(subject, itemId),
    () => consoleApi.moderationItem(itemId, subject),
    { retry: false },
  );

export const useSupportTicketQuery = (ticketId: string) =>
  useApiQuery(queryKeys.support.ticket(ticketId), () => consoleApi.supportTicket(ticketId));

/** The inbox, one status tab at a time, a cursor page at a time. */
export const useSupportTicketsQuery = (status: SupportTicketStatus) =>
  useInfiniteQuery({
    queryKey: queryKeys.support.list(status),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await consoleApi.supportTickets({
          status,
          ...(pageParam ? { cursor: pageParam } : {}),
        }),
      ),
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.nextCursor : undefined),
  });
